// Libs
import classNames from 'classnames/bind';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Grid2, IconButton } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import { IFormService } from '~/utils/interface/service';
import { IQueryParamMaterial, ISelectMaterial } from '~/utils/interface/material';
import useDebounce from '~/utils/hooks/useDebounce';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getMaterialList } from '~/thunks/material/materialThunk';
import { formatInputDecimal } from '~/utils/helper/common';
// Styles, images, icons
import styles from './MaterialService.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const MaterialService = (props: Props) => {
  //#region Destructuring Props
  const { index, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/service']);
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormService>();

  const watchMaterialId = watch(`materials.${index}.materialId`);
  const watchQuantity = watch(`materials.${index}.quantity`);
  const watchUnitCost = watch(`materials.${index}.unitCost`);
  const watchUnitPrice = watch(`materials.${index}.unitPrice`);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [listMaterial, setListMaterial] = useState<ISelectMaterial[]>([]);
  const [isLoadingMaterial, setIsLoadingMaterial] = useState<boolean>(false);
  const [materialsSearchKey, setMaterialsSearchKey] = useState<string>(EMPTY_STRING);
  const debounceMaterialsSearchKey = useDebounce(materialsSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetMaterialsList = useCallback(
    (params: IQueryParamMaterial) => {
      setIsLoadingMaterial(true);

      dispatch(getMaterialList(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;

          if (!responses) return;

          const newData: ISelectMaterial[] = responses.map((item) => ({
            value: item?.id,
            label: item?.name || EMPTY_STRING,
            price: item?.price || EMPTY_STRING,
            unit: item?.unit || EMPTY_STRING,
            quantity: item?.quantity || EMPTY_STRING,
            description: item?.description || EMPTY_STRING,
            cost: item?.cost || EMPTY_STRING,
          }));

          setListMaterial(newData);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingMaterial(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamMaterial = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceMaterialsSearchKey && { textSearch: debounceMaterialsSearchKey }),
    };

    handleGetMaterialsList(params);
  }, [debounceMaterialsSearchKey, handleGetMaterialsList]);

  useEffect(() => {
    setValue(
      `materials.${index}.totalCost`,
      String(Number(watchQuantity) * Number(watchUnitCost)) || EMPTY_STRING,
      {
        shouldValidate: true,
      }
    );
    setValue(
      `materials.${index}.totalPrice`,
      String(Number(watchQuantity) * Number(watchUnitPrice)) || EMPTY_STRING,
      {
        shouldValidate: true,
      }
    );
  }, [watchMaterialId, watchQuantity, watchUnitCost, watchUnitPrice, index, setValue]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOnchangeMaterial = (value: ISelectMaterial) => {
    setValue(`materials.${index}.materialId`, value?.value || EMPTY_STRING, { shouldValidate: true });
    setValue(`materials.${index}.quantity`, value?.quantity || EMPTY_STRING, { shouldValidate: true });
    setValue(`materials.${index}.unitCost`, value?.cost || EMPTY_STRING, { shouldValidate: true });
    setValue(`materials.${index}.unitPrice`, value?.price || EMPTY_STRING, { shouldValidate: true });
  };

  const handleSearchMaterialsChange = (searchKey: string) => {
    setMaterialsSearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='materialServiceComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 container size={12}>
          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.materialId`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  label={t('field_material_name_label')}
                  value={value || EMPTY_STRING}
                  onChange={(optionValue) => handleOnchangeMaterial(optionValue)}
                  options={listMaterial || []}
                  errorMessage={errors?.materials?.[index]?.materialId?.message}
                  onSearchChange={handleSearchMaterialsChange}
                  isLoading={isLoadingMaterial}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.quantity`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_quantity_label')}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputDecimal(event.target.value));
                  }}
                  errorMessage={errors?.materials?.[index]?.quantity?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.unitCost`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_unit_cost_label')}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputDecimal(event.target.value));
                  }}
                  errorMessage={errors?.materials?.[index]?.unitCost?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.unitPrice`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_unit_price_label')}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputDecimal(event.target.value));
                  }}
                  errorMessage={errors?.materials?.[index]?.unitPrice?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.totalCost`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_total_cost_label')}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputDecimal(event.target.value));
                  }}
                  errorMessage={errors?.materials?.[index]?.totalCost?.message}
                  disabled
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`materials.${index}.totalPrice`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_total_price_label')}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputDecimal(event.target.value));
                  }}
                  errorMessage={errors?.materials?.[index]?.totalPrice?.message}
                  disabled
                />
              )}
            />
          </Grid2>

          <Grid2 className={cx('totalField')} width={'auto'}>
            <Grid2 size='auto' className='flex justify-center items-center'>
              <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
                <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
              </IconButton>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default MaterialService;
